import { Component, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartColors } from '../../core/config/chart.config';
import { LineAreaChartComponent } from '../../shared/components/line-area-chart/line-area-chart.component';
import { ChartSeries } from '../../shared/models/chart.model';
import { AuthorAnalyticsService } from './author-analytics.service';
import { Journal, Region } from '../../shared/models/date-filter.model';
import { ChartLegends } from '../../shared/models/chart-legends';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { DataFiltersComponent, FilterOptions } from '../../shared/components/data-filters/data-filters.component';
import { ActivatedRoute } from '@angular/router';
import { ChartFilters, SubmissionsStatsDataRow } from './author-analytics.model';
import { forkJoin, Subscription } from 'rxjs';

@Component({
  selector: 'app-author-analytics',
  standalone: true,
  imports: [
    CommonModule,
    LineAreaChartComponent,
    LoaderComponent,
    DataFiltersComponent
  ],
  templateUrl: './author-analytics.component.html',
  styleUrls: ['./author-analytics.component.scss']
})
export class AuthorAnalyticsComponent implements OnInit, OnDestroy {
  currentFilters: FilterOptions = {
    fromDate: '',
    toDate: '',
    journalId: 'all',
    regionId: 'all',
    institutionId: 'all'
  };

  journals: Journal[] = [];
  regions: Region[] = [];

  fromDate: string = '';
  toDate: string = '';

  activeAuthorsData: any[] = [];
  poolProgressionData: any[] = [];
  marketingOptinData: any[] = [];
  transfersRetentionData: any[] = [];
  submissionsStatsData: SubmissionsStatsDataRow[] = [];
  submittedPublishedDataAvg: any[] = [];
  submissionsCountData: any[] = [];

  activeAuthorsSeries: ChartSeries[] = [{
    key: 'active',
    name: 'Active Authors',
    color: ChartColors.primary
  }];

  poolProgressionSeries: ChartSeries[] = [
    { key: 'new', name: 'New Authors', color: ChartColors.primary },
    { key: 'reactivated', name: 'Reactivated Authors', color: ChartColors.secondary }
  ];

  AvgSubmittedPublishedSeries: ChartSeries[] = [
    { key: 'submitted', name: 'Submitting Authors', color: ChartColors.primary },
    { key: 'published', name: 'Publishing Authors', color: ChartColors.secondary }
  ];

  marketingOptinSeries: ChartSeries[] = [{
    key: 'count',
    name: 'Marketing Optin Count',
    color: ChartColors.primary
  }];

  transfersRetentionSeries: ChartSeries[] = [
    { key: 'retention', name: 'Author Retention Rate', color: ChartColors.primary }
  ];

  submissionsStatsSeries: ChartSeries[] = [
    { key: 'moreThanTwo', name: '% More Than 2 Submissions', color: ChartColors.primary },
    { key: 'moreThanFive', name: '% More Than 5 Submissions', color: ChartColors.secondary },
    { key: 'moreThanTen', name: '% More Than 10 Submissions', color: ChartColors.tertiary }
  ];

  submissionsCountSeries: ChartSeries[] = [
    { key: 'oneSubmission', name: 'One Submission', color: ChartColors.primary },
    { key: 'twoSubmission', name: 'Two Submissions', color: ChartColors.secondary },
    { key: 'threeSubmission', name: 'Three Submissions', color: ChartColors.tertiary },
    { key: 'fourSubmission', name: 'Four Submissions', color: '#8884d8' },
    { key: 'fivePlusSubmission', name: 'Five+ Submissions', color: '#82ca9d' }
  ];

  activeAuthorsLegendInfo = ChartLegends.activeAuthors;
  poolProgressionLegendInfo = ChartLegends.poolProgression;
  submittedPublishedLegendInfo = ChartLegends.submittedPublished;
  submissionsLegendInfo = ChartLegends.submissions;
  transfersLegendInfo = ChartLegends.transfers;
  marketingOptinLegendInfo = ChartLegends.marketingOptin;
  submittedPublishedAvgLegendInfo = ChartLegends.submittedPublishedAvg;
  submissionsCountLegendInfo = ChartLegends.submissionsCount;

  loadingStates: { [key: string]: boolean } = {
    activeAuthors: false,
    poolProgression: false,
    submittedPublishedAvg: false,
    marketingOptin: false,
    transfersRetention: false,
    submissionsStats: false,
    submissionsCount: false
  };

  targetLinePosition: number = 100;
  currentView: 'pool' | 'submissions' = 'pool';

  private loadGeneration = 0;
  private chartLoadSub?: Subscription;
  private routeSub?: Subscription;
  private filtersReady = false;

  constructor(
    private authorAnalyticsService: AuthorAnalyticsService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParams.subscribe(params => {
      const view = params['view'];
      const nextView: 'pool' | 'submissions' =
        view === 'submissions' ? 'submissions' : view === 'pool' ? 'pool' : this.currentView;

      const viewChanged = nextView !== this.currentView;
      if (view === 'pool' || view === 'submissions') {
        this.currentView = nextView;
      }

      if (viewChanged && this.filtersReady && this.currentFilters.fromDate && this.currentFilters.toDate) {
        this.loadChartData(this.currentFilters);
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.chartLoadSub?.unsubscribe();
    this.routeSub?.unsubscribe();
  }

  hasAnyLoadingState(): boolean {
    return Object.values(this.loadingStates).some(state => state);
  }

  onFiltersChanged(filters: FilterOptions): void {
    this.currentFilters = filters;
    this.fromDate = filters.fromDate;
    this.toDate = filters.toDate;
    this.filtersReady = true;
    this.loadChartData(filters);
  }

  private loadChartData(filters: FilterOptions): void {
    this.chartLoadSub?.unsubscribe();
    const generation = ++this.loadGeneration;

    const processed = this.toChartFilters(filters);

    if (this.currentView === 'pool') {
      this.loadPoolCharts(processed, generation);
    } else {
      this.loadSubmissionsCharts(processed, generation);
    }
  }

  private loadPoolCharts(processed: ChartFilters, generation: number): void {
    this.resetLoadingFor(['activeAuthors', 'poolProgression', 'transfersRetention']);

    this.chartLoadSub = forkJoin({
      activeAuthors: this.authorAnalyticsService.getActiveAuthorsData(processed),
      poolProgression: this.authorAnalyticsService.getPoolProgressionData(processed),
      transfersRetention: this.authorAnalyticsService.getTransfersRetention(processed)
    }).subscribe({
      next: result => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.activeAuthorsData = [...(result.activeAuthors ?? [])];
        this.poolProgressionData = [...(result.poolProgression ?? [])];
        this.transfersRetentionData = [...(result.transfersRetention ?? [])];
        this.loadingStates['activeAuthors'] = false;
        this.loadingStates['poolProgression'] = false;
        this.loadingStates['transfersRetention'] = false;
        this.cdr.detectChanges();
      },
      error: err => {
        if (generation !== this.loadGeneration) {
          return;
        }
        console.error('Error loading pool charts', err);
        this.loadingStates['activeAuthors'] = false;
        this.loadingStates['poolProgression'] = false;
        this.loadingStates['transfersRetention'] = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadSubmissionsCharts(processed: ChartFilters, generation: number): void {
    this.resetLoadingFor(['submittedPublishedAvg', 'submissionsStats', 'submissionsCount']);

    this.chartLoadSub = forkJoin({
      submittedPublishedAvg: this.authorAnalyticsService.getAvgSubmittedPublished(processed),
      submissionsStats: this.authorAnalyticsService.getSubmissionsStatsData(processed),
      submissionsCount: this.authorAnalyticsService.getSubmissionsCount(processed)
    }).subscribe({
      next: result => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.submittedPublishedDataAvg = [...(result.submittedPublishedAvg ?? [])];
        this.submissionsStatsData = [...(result.submissionsStats ?? [])];
        this.submissionsCountData = [...(result.submissionsCount ?? [])];
        this.loadingStates['submittedPublishedAvg'] = false;
        this.loadingStates['submissionsStats'] = false;
        this.loadingStates['submissionsCount'] = false;
        this.cdr.detectChanges();
      },
      error: err => {
        if (generation !== this.loadGeneration) {
          return;
        }
        console.error('Error loading submissions charts', err);
        this.loadingStates['submittedPublishedAvg'] = false;
        this.loadingStates['submissionsStats'] = false;
        this.loadingStates['submissionsCount'] = false;
        this.cdr.detectChanges();
      }
    });
  }

  private resetLoadingFor(keys: string[]): void {
    Object.keys(this.loadingStates).forEach(key => {
      this.loadingStates[key] = false;
    });
    keys.forEach(key => {
      this.loadingStates[key] = true;
    });
  }

  private toChartFilters(filters: FilterOptions): ChartFilters {
    const region =
      filters.regionId === 'all' || !filters.regionId ? null : filters.regionId;
    return {
      journal: filters.journalId === 'all' || !filters.journalId ? null : filters.journalId,
      region,
      isoRegion: region
        ? null
        : filters.isoRegionId === 'all' || !filters.isoRegionId
          ? null
          : filters.isoRegionId,
      fromDate: this.convertDateFormat(filters.fromDate),
      toDate: this.convertDateFormat(filters.toDate)
    };
  }

  private convertDateFormat(dateStr: string): string {
    if (!dateStr) {
      console.warn('Empty date string provided to convertDateFormat');
      return '';
    }
    const [year, month] = dateStr.split('-');
    return `${year}${month}`;
  }

  isPool(): boolean {
    return this.currentView === 'pool';
  }

  isSubmissions(): boolean {
    return this.currentView === 'submissions';
  }
}
